interface TabOverflowInput {
  /** Content width of every tab trigger, in source order — what it needs, not what the list gave it. */
  tabWidths: number[];
  /** Width the tab row may occupy. */
  availableWidth: number;
  /** Width of the trailing "More" trigger, reserved as soon as anything overflows. */
  menuButtonWidth: number;
  /** Everything in the row that is not a trigger — the list's own padding and gaps. */
  listChromeWidth: number;
}

/**
 * How many leading tabs stay on one line; everything after them moves into the overflow menu.
 *
 * The split depends on width alone, never on which tab is selected: a row that reshuffled itself on
 * selection would move the tab under the pointer out from under it. When the selection ends up in the
 * menu, the menu trigger carries the marker instead.
 *
 * Widths must come from a row that always renders every tab. Measuring the row we are collapsing
 * would feed its own output back in — one tab hidden frees the width that put it there.
 */
export function computeVisibleTabCount({
  tabWidths,
  availableWidth,
  menuButtonWidth,
  listChromeWidth
}: TabOverflowInput): number {
  // A row that has not been laid out yet reports 0; showing everything matches the pre-measure render.
  if (availableWidth <= 0) {
    return tabWidths.length;
  }

  const totalWidth = tabWidths.reduce((sum, width) => sum + width, listChromeWidth);
  if (totalWidth <= availableWidth) {
    return tabWidths.length;
  }

  const budget = availableWidth - listChromeWidth - menuButtonWidth;
  const runningWidths = tabWidths.reduce<number[]>(
    (widths, width) => [...widths, (widths[widths.length - 1] ?? 0) + width],
    []
  );
  const firstOverflow = runningWidths.findIndex((width) => width > budget);

  return firstOverflow === -1 ? runningWidths.length : firstOverflow;
}
